/**
 * @file PermissionsTable.jsx
 * @description Permissions Table component for displaying and managing permissions.
 * @component PermissionsTable
 * @exports PermissionsTable - Main component for displaying and managing permissions.
 */

import React from "react";
import Table from "@mui/material/Table";
import { Edit } from "@mui/icons-material";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { PERMISSION_TABLE_COLUMN_KEYS, PERMISSION_MESSAGES } from "../../../common/constants/adminModule/permissionConstants.js";

const PermissionsTable = ({
    page,
    order,
    setPage,
    orderBy,
    roles,
    isLoading,
    rowsPerPage,
    onEdit,
    setRowsPerPage,
    columns,
    handleRequestSort,
    filteredRolesLength,
}) => {
    return (
        <>
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    sx={{
                                        width:
                                            column.id === PERMISSION_TABLE_COLUMN_KEYS.ACTIONS ? 50 :
                                                    column.id === PERMISSION_TABLE_COLUMN_KEYS.ROLE_NAME ? 200 : 150,
                                    }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() =>
                                                handleRequestSort(column.id)
                                            }
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : roles.length > 0 ? roles.map((role) => (
                            <TableRow hover tabIndex={-1} key={role.id}>
                                <TableCell>
                                    <IconButton
                                        size={SIZES.SMALL}
                                        onClick={() => onEdit({roleId: role.id, rolename: role.rolename})}
                                    >
                                        <Edit fontSize={SIZES.SMALL} />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    {role.rolename}
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {PERMISSION_MESSAGES.NO_ROLES}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredRolesLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>
        </>
    )
};

export default PermissionsTable;